import {NavigationContainer} from '@react-navigation/native';
import querystring from 'querystring';
import * as React from 'react';
import {useCallback, useEffect, useRef, useState} from 'react';
import {AppState, Linking, Platform, StatusBar, StyleSheet, View} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-view';
import {useDispatch, useSelector} from 'react-redux';
import {Dispatch} from 'redux';
import {Routes} from '.';
import {enter, esiaLoginFromActiveUser, logout, resetLoginState} from '../../redux/actions/login';
import {getECardInfo, getHolderProfile, getProfileNotifications, getProfileSettings} from '../../redux/actions/profile';
import {getContent} from '../../redux/actions/resources';
import {compareRoutesToHideGoToApp} from '../../redux/actions/runtime/menu';
import {profileSlice} from '../../redux/reducers/profile';
import {
  holderProfileSelector,
  profileAuthenticationStateSelector,
  profileSessionExpiredSelector,
  profileTokenSelector,
} from '../../redux/selectors/profile';
import {PinCodeAuthentication} from '../../screens/login/pinCodeAuthentication';
import {useModal} from '../components/modal';
import {Preloader} from '../components/preloader';
import {codeLinkScheme, deepLinkScheme as deepLinkPrefix} from '../constants';
import {fcmService, useFcmWrapper} from '../fcmWrapper';
import {useDimensions} from '../hooks/useDimensions';
import {throttle} from '../pipes';
import {storage, StorageKeys} from '../storage';
import {useTheme} from '../theme';
import {DeviceSize, useDeviceSize} from '../theme/helpers';
import AppRedirectForm from './app-redirect-form';
import {DesktopStackScreen} from './desktop';
import {DrawerMenu} from './drawer';
import {linkingDesktop, linkingLogin, linkingMobile} from './linking';
import {LoginScreens} from './login';
import {loginSlice} from '../../redux/reducers/login';
import {setRuntimeLoginStepState} from '../../redux/actions/runtime/loginStep';
import {LoginState} from '../models/login/loginStep';
import {esiaRegistrationInfoSelector} from '../../redux/selectors/login';
import {loginStepSlice} from '../../redux/reducers/runtime/loginStep';

const maxNonBlockingTime = 60 * 1000;

interface DeepLinkInput {
  url: string | null;
}

export const Navigation = () => {
  const dispatch = useDispatch();
  const modals = useModal();
  useFcmWrapper();

  const [isAppDataRestored, setAppDataRestored] = useState(false);
  const esiaRegInfo = useSelector(esiaRegistrationInfoSelector);
  useEffect(() => {
    (async () => {
      const token = await storage.getItem<string>(StorageKeys.token);
      const pinCode = await storage.getItem<string>(StorageKeys.pinCode);
      if (pinCode) {
        dispatch(profileSlice.actions.setPinCode(pinCode));
      }
      if (token) {
        if (pinCode || Platform.OS === 'web') {
          dispatch(enter(token));
        } else {
          dispatch(logout());
        }
      }
      setAppDataRestored(true);
    })();
  }, []);

  const token = useSelector(profileTokenSelector());
  const isSessionExpired = useSelector(profileSessionExpiredSelector());
  let isAuthenticated = useSelector(profileAuthenticationStateSelector());
  const isAuthenticatedByPassword = token && !isSessionExpired;
  const backgroundStartTimestamp = useRef<null | number>(null);

  if (Platform.OS === 'web' && isAuthenticatedByPassword && !isAuthenticated) {
    dispatch(profileSlice.actions.setAuthenticationState(true));
  }

  if (Platform.OS !== 'web') {
    if (backgroundStartTimestamp.current && AppState.currentState === 'active') {
      if (Date.now() - backgroundStartTimestamp.current > maxNonBlockingTime) {
        dispatch(profileSlice.actions.setAuthenticationState(false));
        isAuthenticated = false; // Страшная вещь, чтобы модалка появилась раньше
      }

      backgroundStartTimestamp.current = null;
    }

    if (!backgroundStartTimestamp.current && AppState.currentState !== 'active') {
      backgroundStartTimestamp.current = Date.now();
    }
  }

  const addHandlerToUrl = async (storeDeepLink: (obj: DeepLinkInput) => void) => {
    const initialUrl = await Linking.getInitialURL();
    storeDeepLink({url: initialUrl});
    Linking.addEventListener('url', storeDeepLink);
  };

  const focusHandler = useCallback(() => {
    if (Platform.OS === 'web') {
      return;
    }
    let isEffectInstanceActive = true;
    const storeDeepLink = ({url}: DeepLinkInput) => {
      if (url?.startsWith(deepLinkPrefix) && url?.includes('op=')) {
        const deepLink = url;
        const keyRoute = '/?link=confirmation';
        if (deepLink && deepLink.indexOf(keyRoute) > -1) {
          const searchedKeyword = isAuthenticated ? 'op=change' : 'op=restore';
          const searchedKeywordForEmailChange = 'op=changeEmail';
          const replaceRoute = isAuthenticated ? Routes.profileConfirmation : Routes.loginConfirmation;
          const isDeepLinkIncludesKeywords = deepLink.indexOf(searchedKeyword) > -1;
          const isDeepLinkIncludesKeywordsForEmailChange = deepLink.indexOf(searchedKeywordForEmailChange) > -1;
          const replaceRouteForEmailChange = Routes.profileConfirmation;
          if (!isDeepLinkIncludesKeywords && !isDeepLinkIncludesKeywordsForEmailChange) {
            return;
          }
          const linkToOpen = deepLink;
          if (!isEffectInstanceActive) {
            return;
          }
          setTimeout(() => {
            if (isEffectInstanceActive) {
              Linking.openURL(
                linkToOpen.replace(
                  keyRoute,
                  `${isDeepLinkIncludesKeywordsForEmailChange ? replaceRouteForEmailChange : replaceRoute}?`,
                ),
              );
            }
          }, 100);
        }
      }
    };

    addHandlerToUrl(storeDeepLink);

    return () => {
      isEffectInstanceActive = false;
      Linking.removeEventListener('url', storeDeepLink);
    };
  }, []);

  useEffect(focusHandler, []);

  useEffect(() => {
    let modalId: string | null = null;

    if (isAuthenticatedByPassword && !isAuthenticated) {
      modalId = modals.show({
        render: () => <PinCodeAuthentication />,
        mobile: true,
      });
    }

    return () => {
      !!modalId && modals.remove(modalId);
    };
  }, [isAuthenticatedByPassword, isAuthenticated]);

  if (!isAppDataRestored) {
    return null;
  }

  if (Platform.OS === 'web' && window?.location.pathname === codeLinkScheme) {
    const link = window.location.search;
    if (typeof link === 'string' && link.length) {
      return <AppRedirectForm link={link} />;
    }
  }

  if (Platform.OS === 'web' && window?.location.pathname === '/') {
    const {code, state} = querystring.parse(window.location.search.replace('?', '')) ?? {};
    if (!code || !state) {
      dispatch(resetLoginState());
    } else {
      dispatch(loginSlice.actions.setEsiaCode({code, state}));
      if (isAuthenticated) dispatch(esiaLoginFromActiveUser());
    }
    dispatch(setRuntimeLoginStepState(LoginState.enter));
  }

  if (!isAuthenticatedByPassword) {
    return <LoginContainer />;
  }

  return <AuthorizedContainer />;
};

export const initialLoading = async (dispatch: Dispatch<any>) => {
  // ToDo jwt token;
  await Promise.all([
    dispatch(getHolderProfile()),
    dispatch(getProfileSettings()),
    // dispatch(getProfilePhoto()),
    dispatch(getProfileNotifications()),
    dispatch(getECardInfo()),
  ]);
  // eslint-disalbe-next-line
  await dispatch(getContent());
};

export const syncFCMState = async (dispatch: Dispatch<any>) => {
  const isFcmConnected = await fcmService.isConnected();
  if (!isFcmConnected && Platform.OS === 'web') {
    throttle(async () => {
      // add here all methods to perform manual refresh
      dispatch(getProfileNotifications());
    }, 2000);
  }
};

const onNavigatorStateChange = (dispatch: Dispatch<any>) => {
  syncFCMState(dispatch);
};

const DesktopNavigation = () => {
  const theme = useTheme();
  const {height} = useDimensions();
  const dispatch = useDispatch();

  return (
    <View style={{height}}>
      <NavigationContainer
        theme={theme.navigation}
        linking={linkingDesktop}
        onStateChange={() => onNavigatorStateChange(dispatch)}>
        <DesktopStackScreen />
      </NavigationContainer>
    </View>
  );
};

const MobileNavigation = () => {
  const theme = useTheme();
  const [isInitRender, setIsInitRender] = useState(true);
  const dispatch = useDispatch();

  useEffect(() => {
    if (Platform.OS === 'android') {
      StatusBar.setBackgroundColor(theme.color.grayMenu, true);
    }
    StatusBar.setBarStyle('dark-content', true);
  }, []);

  useEffect(() => {
    setIsInitRender(false);
  }, [isInitRender]);

  return (
    <SafeAreaProvider style={StyleSheet.absoluteFill}>
      <View
        style={{
          flex: 1,
          display: isInitRender ? 'none' : undefined,
          overflow: 'hidden',
        }}>
        <NavigationContainer
          theme={theme.navigation}
          linking={linkingMobile}
          fallback={<Preloader type='main' />}
          onStateChange={(e) => {
            dispatch(compareRoutesToHideGoToApp(e));
            onNavigatorStateChange(dispatch);
          }}>
          <DrawerMenu />
        </NavigationContainer>
      </View>
    </SafeAreaProvider>
  );
};

const LoginContainer = () => {
  const {height} = useDimensions();

  useEffect(() => {
    if (Platform.OS === 'android') {
      StatusBar.setBackgroundColor('#0070B3', true);
    }
    StatusBar.setBarStyle('light-content', true);
  }, []);

  return (
    <View style={{height}}>
      <NavigationContainer linking={linkingLogin} fallback={<Preloader type='login' />}>
        <LoginScreens />
      </NavigationContainer>
    </View>
  );
};

const AuthorizedContainer = () => {
  const size = useDeviceSize();

  switch (size) {
    case DeviceSize.tablet:
    case DeviceSize.mobile:
      return <MobileNavigation />;
    default:
      return <DesktopNavigation />;
  }
};
